import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import type { ApiErrorBody, LoginResponse } from '@bsl/shared';

/** Normalised API error. Components switch on `code`; `details` map onto form fields. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details: { path: string; message: string }[] = [],
    public readonly traceId?: string,
  ) {
    super(message);
  }
}

export const http = axios.create({ baseURL: '/api', withCredentials: true, timeout: 60_000 });

interface SessionHooks {
  getToken: () => string | null;
  onRefreshed: (res: LoginResponse) => void;
  onSessionLost: () => void;
}

let hooks: SessionHooks | null = null;
let refreshing: Promise<LoginResponse> | null = null;

export function bindSession(h: SessionHooks) {
  hooks = h;
}

/** One refresh at a time: concurrent 401s wait for the same refresh call. */
export function refreshSession(): Promise<LoginResponse> {
  refreshing ??= axios
    .post<LoginResponse>('/api/auth/refresh', {}, { withCredentials: true })
    .then((r) => {
      hooks?.onRefreshed(r.data);
      return r.data;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

http.interceptors.request.use((config) => {
  const token = hooks?.getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const RETRYABLE_AUTH_CODES = new Set(['TOKEN_INVALID', 'TOKEN_REVOKED', 'AUTH_REQUIRED']);

http.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<ApiErrorBody>) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const status = error.response?.status ?? 0;
    const code = error.response?.data?.code;

    if (status === 401 && original && !original._retried && code && RETRYABLE_AUTH_CODES.has(code) && !original.url?.startsWith('/auth/')) {
      original._retried = true;
      try {
        await refreshSession();
        return http(original);
      } catch {
        hooks?.onSessionLost();
      }
    }
    throw toApiError(error);
  },
);

export async function toApiErrorFromBlob(error: AxiosError): Promise<ApiError> {
  const data = error.response?.data;
  if (data instanceof Blob) {
    try {
      const body = JSON.parse(await data.text()) as ApiErrorBody;
      return new ApiError(error.response!.status, body.code, body.message, body.details, body.traceId);
    } catch {
      /* fall through */
    }
  }
  return toApiError(error as AxiosError<ApiErrorBody>);
}

function toApiError(error: AxiosError<ApiErrorBody>): ApiError {
  if (!error.response) {
    return new ApiError(0, 'NETWORK_ERROR', 'Cannot reach the server. Check your connection and try again.');
  }
  const body = error.response.data;
  if (body && typeof body === 'object' && 'code' in body) {
    return new ApiError(error.response.status, body.code, body.message, body.details ?? [], body.traceId);
  }
  return new ApiError(error.response.status, 'ERROR', error.message);
}

export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) => http.get<T>(url, config).then((r) => r.data),
  post: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) => http.post<T>(url, body, config).then((r) => r.data),
  put: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) => http.put<T>(url, body, config).then((r) => r.data),
  patch: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) => http.patch<T>(url, body, config).then((r) => r.data),
  delete: <T>(url: string, config?: AxiosRequestConfig) => http.delete<T>(url, config).then((r) => r.data),
};

/** Downloads a server-generated file (export) through the authenticated client. */
export async function downloadFile(url: string, params: Record<string, unknown>, fallbackName: string) {
  try {
    const res = await http.get<Blob>(url, { params, responseType: 'blob' });
    const disposition = String(res.headers['content-disposition'] ?? '');
    const name = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? fallbackName;
    const href = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw await toApiErrorFromBlob(err as AxiosError);
  }
}

/** Opens a server-generated PDF in a new tab (falls back to download if pop-ups are blocked). */
export async function openPdf(url: string, fallbackName: string) {
  // Open the tab synchronously (inside the click) so pop-up blockers allow it, then point it at the blob.
  const tab = window.open('', '_blank');
  try {
    const res = await http.get<Blob>(url, { responseType: 'blob' });
    const href = URL.createObjectURL(res.data);
    if (tab) tab.location.href = href;
    else {
      const a = document.createElement('a');
      a.href = href;
      a.download = fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(href), 60_000);
  } catch (err) {
    tab?.close();
    if (err instanceof ApiError) throw err;
    throw await toApiErrorFromBlob(err as AxiosError);
  }
}
